// ─── NotificationBell.jsx ─────────────────────────────────────────────────────
// Bell icon with unread badge + dropdown + polling for notifications.
// Import as: import NotificationBell from "./NotificationBell";
//
// Wire into App.jsx Sidebar:
//   import NotificationBell from "./NotificationBell";
//   // Inside sidebar header div (next to company pill), add:
//   <NotificationBell currentUser={currentUser} company={company} onNavigate={setActive} />

import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { db } from "./dbClient";

const TYPE_ICON = {
  alert_assigned:    "◈",
  case_updated:      "◎",
  todo_assigned:     "✓",
  threshold_changed: "⇄",
  sar_ready:         "◉",
  sar_filed:         "◉",
  risk_score_set:    "🪪",
  chat_message:      "💬",
  message:           "💬",
  default:           "●",
};

export default function NotificationBell({ currentUser, company, onNavigate }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [open,    setOpen]    = useState(false);
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef();

  const unread = notifs.filter(n => !n.read).length;

  // Load notifications via API and poll periodically
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadNotifications = async () => {
      try {
        setLoading(true);
        const result = await db.from("notifications")
          .select("*")
          .eq("user_id", currentUser.id)
          .order("created_at", false)
          .limit(40)
          .execute();

        const data = result.data || [];
        setNotifs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.warn('[NotificationBell] Failed to load notifications (table may not exist):', error.message?.substring(0, 100));
        setNotifs([]);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [currentUser?.id]);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function markAllRead() {
    const ids = notifs.filter(n => !n.read).map(n => n.id);
    if (!ids.length) return;

    try {
      for (const id of ids) {
        await db.from("notifications").update({ read: true }).eq("id", id).execute();
      }
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.warn('[NotificationBell] Failed to mark all read:', error.message?.substring(0, 100));
    }
  }

  async function handleClick(n) {
    if (!n.read) {
      try {
        await db.from("notifications").update({ read: true }).eq("id", n.id).execute();
        setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      } catch (error) {
        console.warn('[NotificationBell] Failed to mark read:', error.message?.substring(0, 100));
      }
    }
    if (n.link_module) onNavigate(n.link_module);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        title={t('notifications.title')}
        style={{ background: open ? C.accentGlow : "transparent",
          border: `1px solid ${open ? C.borderLight : "transparent"}`,
          borderRadius: 6, cursor: "pointer", color: C.textMid,
          width: 30, height: 30, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 14, position: "relative",
          transition: "all 0.12s" }}
      >
        🔔
        {unread > 0 && (
          <span style={{ position: "absolute", top: 2, right: 2,
            background: C.danger, color: "#fff", fontSize: 8, fontWeight: 700,
            borderRadius: "50%", minWidth: 14, height: 14,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 2px", lineHeight: 1 }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{ position: "absolute", left: "100%", top: 0, marginLeft: 8,
          width: 320, background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
          zIndex: 9999, overflow: "hidden" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "12px 16px",
            borderBottom: `1px solid ${C.border}` }}>
            <span style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>
              {t('notifications.title')}
            </span>
            {unread > 0 && (
              <button onClick={markAllRead}
                style={{ background: "transparent", border: "none",
                  color: C.accent, fontSize: 11, cursor: "pointer",
                  fontFamily: "'DM Mono', monospace" }}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: "center",
                color: C.textDim, fontSize: 12 }}>{t('common.loading')}</div>
            ) : notifs.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center",
                color: C.textDim, fontSize: 12 }}>
                <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }}>🔔</div>
                {t('notifications.noNotifications')}
              </div>
            ) : notifs.map(n => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}`,
                  background: n.read ? "transparent" : C.accentGlow,
                  cursor: "pointer", display: "flex", gap: 10,
                  alignItems: "flex-start", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                onMouseLeave={e => e.currentTarget.style.background = n.read ? "transparent" : C.accentGlow}
              >
                <span style={{ color: C.accent, fontSize: 13, marginTop: 1, flexShrink: 0 }}>
                  {TYPE_ICON[n.type] || TYPE_ICON.default}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 12,
                    fontWeight: n.read ? 400 : 600, lineHeight: 1.4 }}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div style={{ color: C.textDim, fontSize: 11,
                      marginTop: 2, lineHeight: 1.4 }}>
                      {n.body}
                    </div>
                  )}
                  <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read && (
                  <div style={{ width: 6, height: 6, borderRadius: "50%",
                    background: C.accent, flexShrink: 0, marginTop: 4 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}